// Global endpoint instance (set by AgentBuilderProvider)
let globalEndpoint: string | null = null

/**
 * Internal function to set the global endpoint.
 * Called by AgentBuilderProvider when it mounts.
 * @internal
 */
export function __setGlobalEndpointForStop(endpoint: string) {
  globalEndpoint = endpoint
}

export interface StopThreadOptions {
  /**
   * Override the endpoint URL for this request.
   * If not provided, uses the endpoint from AgentBuilderProvider.
   */
  endpoint?: string
}

/**
 * Stop execution of a thread.
 *
 * This is a standalone function that stops a running thread execution.
 * It requires that an AgentBuilderProvider is mounted somewhere in your app
 * to set the global endpoint configuration, or you can provide a custom endpoint.
 *
 * @param id - The thread ID to stop
 * @param options - Optional configuration including custom endpoint
 * @returns Promise that resolves when the thread is stopped
 *
 * @throws Error if called before AgentBuilderProvider is mounted and no endpoint is provided
 *
 * @example
 * ```tsx
 * import { stopThread } from '@standardagents/react'
 *
 * // Using global endpoint from AgentBuilderProvider
 * await stopThread('thread-123')
 *
 * // Using custom endpoint
 * await stopThread('thread-123', {
 *   endpoint: 'https://custom.example.com/api'
 * })
 * ```
 */
export async function stopThread(
  id: string,
  options?: StopThreadOptions
): Promise<void> {
  const endpoint = options?.endpoint ?? globalEndpoint

  if (!endpoint) {
    throw new Error(
      'stopThread requires AgentBuilderProvider to be mounted or endpoint option to be provided'
    )
  }

  // Normalize endpoint by removing trailing slash
  const normalizedEndpoint = endpoint.replace(/\/$/, '')

  // Read auth token from localStorage
  const token =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('agentbuilder_auth_token')
      : null

  // Build headers
  const headers: Record<string, string> = {}

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  // Make the request
  const response = await fetch(`${normalizedEndpoint}/threads/${id}/stop`, {
    method: 'POST',
    headers,
  })

  if (!response.ok) {
    throw new Error(`Failed to stop thread: ${response.statusText}`)
  }

  await response.json()
}
